import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

// Core
import { Observable } from 'rxjs/Observable';
import { appConfig } from '../../config/app-config';
import { BoolResult, IBoolResult } from '../../core/interfaces/bool-result';
import { ErrorMessage } from '../../core/model/error-message';
import { UiMessageSnackService } from '../components/message-snack/service/message-snack.service';

interface IHttpWrapperParamsBase {
   url: string;
   options: any;
   debugMode: boolean;
}

interface IHttpWrapperParamsPayload extends IHttpWrapperParamsBase {
   method: 'put' | 'post' | 'patch';
   body: any;
}
interface IHttpWrapperParamsNoPayload extends IHttpWrapperParamsBase {
   method: 'get' | 'delete';
}

type IHttpWrapperParams = IHttpWrapperParamsPayload | IHttpWrapperParamsNoPayload;

export const defaultErrorMessage = 'Bei der Aktion ist ein Fehler aufgetreten!';

@Injectable()
export class DataHubService {

   constructor(
      protected http: HttpClient,
      protected log: UiMessageSnackService
   ) {

   }

   private async httpWrapper(params: IHttpWrapperParams): Promise<any> {
      if (params.debugMode) {
         this.log.logMessage('INFO', {
            consoleMessage: `${params.method} ${params.url}`,
            module: 'datahub.service',
         });
      }

      try {
         let obs: Observable<any>;
         switch (params.method) {
            case 'delete':
            case 'get':
               obs = this.http[params.method](
                  params.url,
                  params.options
               );
               break;

            case 'put':
            case 'post':
            case 'patch':
               obs = this.http[params.method](
                  params.url,
                  params.body,
                  params.options
               );
               break;
         }
         return await obs.toPromise();
      } catch (err) {
         if (err.status === 401) {
            throw new ErrorMessage('Unauthorized', 'Sie haben nicht die nötigen Rechte für diese Aktion!', 'Unauthorized', err);
         }
         throw new ErrorMessage(
            'HttpErrorResponse',
            defaultErrorMessage,
            'HttpErrorResponse',
            err);
      }

   }

   protected httpGetAsJsonWithParams(
      url: string,
      params: HttpParams,
      debugMode: boolean = false
   ): Promise<any> {
      return this.httpWrapper({
         method: 'get',
         url,
         options: {
            headers: new HttpHeaders({
               'Content-Type': 'application/json',
               'Accept': 'application/json'
            }),
            params: params,
            responseType: 'json'
         },
         debugMode
      });
   }

   protected httpGetAsBlob(
      url: string,
      options: any = {
         responseType: 'blob',
      },
      debugMode: boolean = false,
   ): Promise<any> {
      return this.httpWrapper({
         method: 'get',
         url,
         options,
         debugMode,
      });
   }

   protected httpPostAsBlob(
      url: string,
      debugMode: boolean = false,
      body?: any,
   ): Promise<any> {
      return this.httpWrapper({
         method: 'post',
         url,
         options: {
            responseType: 'blob',
         },
         body,
         debugMode,
      });
   }

   protected httpPostAsJsonWithParams(
      url: string,
      params: HttpParams,
      body: any,
      debugMode: boolean = false
   ): Promise<any> {
      return this.httpWrapper({
         method: 'post',
         url,
         options: {
            params: params,
            responseType: 'json'
         },
         body,
         debugMode
      });
   }

   protected httpPutAsJsonWithParams(
      url: string,
      params: HttpParams,
      body: any,
      debugMode: boolean = false
   ): Promise<any> {
      return this.httpWrapper({
         method: 'put',
         url,
         options: {
            params: params,
            responseType: 'json'
         },
         body,
         debugMode
      });
   }

   httpPatchAsJsonWithParams(
      url,
      params: HttpParams,
      body: any,
      debugMode: boolean = false          // DebugMode
   ): Promise<any> {
      return this.httpWrapper({
         method: 'patch',
         url,
         body,
         options: { responseType: 'text' },
         debugMode
      });
   }

   protected httpDelete(
      url,
      debugMode: boolean = false
   ): Promise<null> {
      return this.httpWrapper({
         method: 'delete',
         url,
         options: { responseType: 'text' },
         debugMode
      });
   }

   protected readFileMapJsonArrayToClass<OT>(
      filepath: string,                   // file to read
      keyOfValue: string,                 // key in json to take the array
      result: OT[],                       // output
      jsObjToClass: (param: any) => OT,   // mapping function from JSON to Mangel
      debugMode: boolean = false          // DebugMode
   ): Promise<IBoolResult> {
      if (debugMode) { console.log('[DataHubService]:readFileMapJsonArrayToClass(called)'); }

      const prom = new Promise<IBoolResult>((resolve, reject) => {

         this.http
            .get(filepath, { responseType: 'json' })
            .subscribe((res: any) => {

               // array available
               if (res[keyOfValue] && typeof res[keyOfValue] === 'object') {

                  try {
                     // map json array to object
                     this.mapJsonArrayToClass<OT>(
                        res[keyOfValue],        // data from Json file
                        result,                 // array to take maped results
                        jsObjToClass,           // map functions
                        debugMode               // debug mode
                     );

                  } catch (e) {
                     console.warn(e);
                     reject(new ErrorMessage('mangel/INTERNAL_ERROR', 'Mapping von Json zu Object fehlgeschlagen'));
                     return;
                  }

                  // success: <<-- pass result
                  if (result.length > 0) {
                     // number > 0
                     setTimeout(() => {     // simulate latency
                        resolve(BoolResult.createBoolResult_true());
                     }, appConfig.dev.durationDelayForMockedData);
                  } else {
                     // number = 0
                     setTimeout(() => {     // simulate latency
                        resolve(BoolResult.createBoolResult_falseNoMsg());
                     }, appConfig.dev.durationDelayForMockedData);
                  }
                  return;
               }

               // error: data not available
               console.warn('JSON mock Daten: Datenformat ungültig');
               reject(new ErrorMessage('mangel/MANGEL_HTML_ERROR', 'Datenformat ungültig'));
            },
               (err: HttpErrorResponse) => {
                  reject(new ErrorMessage(
                     'mangel/HttpErrorResponse',
                     err.message,
                     'HttpErrorResponse',
                     err));
               }
            );

      });
      return prom;
   }

   protected readFileMapJsonObjectToClass<OT>(
      filepath: string,                   // file to read
      keyOfValue: string,                 // key in json to take the array
      result: OT[],                       // output
      jsObjToClass: (param: any) => OT,   // mapping function from JSON to Mangel
      debugMode: boolean = false          // DebugMode
   ): Promise<IBoolResult> {
      if (debugMode) { console.log('[DataHubService]:readFileMapJsonObjectToClass(called)'); }

      const prom = new Promise<IBoolResult>((resolve, reject) => {

         this.http
            .get(filepath, { responseType: 'json' })
            .subscribe((res: any) => {

               // array available
               if (res[keyOfValue] && typeof res[keyOfValue] === 'object') {

                  try {
                     // map json array to object
                     this.mapJsonObjectToClass<OT>(
                        res[keyOfValue],           // data from Json file
                        result,                    // array to take maped results
                        jsObjToClass,              // map functions
                        debugMode                  // debug mode
                     );
                  } catch (e) {
                     console.warn(e);
                     reject(new ErrorMessage('mangel/INTERNAL_ERROR', 'Mapping von Json zu Object fehlgeschlagen'));
                     return;
                  }

                  // success: <<-- pass result
                  if (result.length > 0) {
                     // number > 0
                     setTimeout(() => {     // simulate latency
                        resolve(BoolResult.createBoolResult_true());
                     }, appConfig.dev.durationDelayForMockedData);
                  } else {
                     // number = 0
                     setTimeout(() => {     // simulate latency
                        resolve(BoolResult.createBoolResult_falseNoMsg());
                     }, appConfig.dev.durationDelayForMockedData);
                  }
                  return;
               }

               // error: data not available
               console.warn('JSON mock Daten: Datenformat ungültig');
               reject(new ErrorMessage('mangel/MANGEL_HTML_ERROR', 'Datenformat ungültig'));
            },
               (err: HttpErrorResponse) => {
                  reject(new ErrorMessage(
                     'mangel/HttpErrorResponse',
                     err.message,
                     'HttpErrorResponse',
                     err));
               }
            );

      });
      return prom;
   }

   protected readFileJsonSchema(
      filepath: string,                // file to read
      debugMode: boolean = false       // DebugMode
   ): Promise<any> {
      if (debugMode) { console.log('[DataHubService]:readFileJsonSchema(called)'); }

      const prom = new Promise<any>((resolve, reject) => {

         this.http
            .get(filepath, { responseType: 'json' })
            .subscribe((fs: any) => {
               setTimeout(() => {     // simulate latency
                  resolve(fs);
               }, appConfig.dev.durationDelayForMockedData);
            },
               (err: HttpErrorResponse) => {
                  reject(new ErrorMessage(
                     'mangel/HttpErrorResponse',
                     err.message,
                     'HttpErrorResponse',
                     err));
               }
            );

      });
      return prom;
   }

   protected writeFileJsonSchema(
      filepath: string,                // file to write
      data: any,
      debugMode: boolean = false       // DebugMode
   ): Promise<any> {
      if (debugMode) { console.log('[DataHubService]:writeFileJsonSchema(called)'); }

      const prom = new Promise<any>((resolve, reject) => {

         reject(new ErrorMessage('dataHub/WRITE_JSON_FILE', 'Feature noch nicht implementiert'));

      });
      return prom;
   }

   // --------------------------------------------------------------------------------------------
   // -- Mapping ---------------------------------------------------------------------------------
   // --------------------------------------------------------------------------------------------

   protected mapJsonObjectToClass<OT>(
      jsonObject: any,
      result: OT[],                       // output
      jsObjToClass: (param: any) => OT,   // mapping function from JSON to Mangel
      debugMode: boolean = false          // DebugMode
   ) {
      result.push(jsObjToClass(jsonObject));
   }

   protected mapJsonArrayToClass<OT>(
      jsonArray: any,
      result: OT[],                       // output
      jsObjToClass: (param: any) => OT,   // mapping function from JSON to Mangel
      debugMode: boolean = false          // DebugMode
   ) {
      for (let i = 0; i < jsonArray.length; i++) {
         result.push(jsObjToClass(jsonArray[i]));
      }
   }
}



// WEBPACK FOOTER //
// /builds/cp/frap/t3/planer/src/app/core/datahub/datahub.service.ts